import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, Loader2, BarChart3, RefreshCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { CHANNEL_OPTIONS, QUALITY_OPTIONS } from './OutcomePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  krankenhaus: 'Krankenhaus',
  stadtverwaltung: 'Stadtverwaltung',
  versicherung: 'Versicherung',
  bank_sparkasse: 'Bank / Sparkasse',
  stadtwerke_energie: 'Stadtwerke / Energie',
  versandhandel: 'Versandhandel',
  krankenkasse: 'Krankenkasse',
  verlag_medien: 'Verlag / Medien',
  kanzlei: 'Kanzlei',
  industrie: 'Industrie',
  lettershop_druckerei: 'Lettershop / Druckerei',
  it_telekom: 'IT / Telekom',
  einzelhandel: 'Einzelhandel',
  sonstige: 'Sonstige',
};

const QUALITY_LABELS = Object.fromEntries(QUALITY_OPTIONS.map((o) => [o.value, o.label]));
const CHANNEL_LABELS = Object.fromEntries(CHANNEL_OPTIONS.map((o) => [o.value, o.label]));

const KPI = ({ label, value, suffix, color = 'text-slate-900', testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${color}`}>
      {value}{suffix && <span className="text-sm text-slate-400 font-normal ml-1">{suffix}</span>}
    </div>
  </div>
);

const Bar = ({ label, value, max, hint }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mb-1.5">
      <div className="flex items-baseline justify-between text-xs mb-0.5">
        <span className="text-slate-700 truncate">{label}</span>
        <span className="text-slate-500 ml-2 flex-shrink-0">{hint}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: '#FFA266' }}
        />
      </div>
    </div>
  );
};

const Section = ({ title, buckets, labelMap = {} }) => {
  const entries = Object.entries(buckets || {})
    .filter(([k]) => k && k !== 'unbekannt')
    .sort((a, b) => (b[1].response_rate_pct || 0) - (a[1].response_rate_pct || 0));
  const max = Math.max(1, ...entries.map(([, v]) => v.first_contacts || 0));
  if (entries.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-xs text-slate-400 italic">Noch keine Daten erfasst.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">{title}</h3>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500">
            <th className="text-left pb-2">Segment</th>
            <th className="text-right pb-2">Erstkontakte</th>
            <th className="text-right pb-2">Antwortquote</th>
            <th className="text-right pb-2">Mandat-Conv.</th>
            <th className="text-right pb-2">⌀ Tage</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, v]) => (
            <tr key={key} className="border-t border-slate-100">
              <td className="py-2 pr-2 align-top">
                <div className="font-medium text-slate-900 truncate">{labelMap[key] || key}</div>
                <div className="mt-1 max-w-[200px]">
                  <Bar label="" value={v.first_contacts} max={max} />
                </div>
              </td>
              <td className="text-right text-slate-700 align-top">{v.first_contacts}</td>
              <td className="text-right align-top">
                <span className={v.response_rate_pct >= 25 ? 'text-emerald-700 font-semibold' : 'text-slate-700'}>
                  {v.response_rate_pct}%
                </span>
              </td>
              <td className="text-right align-top">
                <span className={v.conv_meeting_to_mandate_pct >= 25 ? 'text-emerald-700 font-semibold' : 'text-slate-700'}>
                  {v.conv_meeting_to_mandate_pct}%
                </span>
              </td>
              <td className="text-right text-slate-500 align-top">
                {v.median_days_to_response != null ? `${v.median_days_to_response} d` : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StatsTab = ({ password }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterChannel, setFilterChannel] = useState('');
  const [filterBranche, setFilterBranche] = useState('');
  const [filterState, setFilterState] = useState('');
  const [since, setSince] = useState('');

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterChannel) params.set('channel', filterChannel);
      if (filterBranche) params.set('branche', filterBranche);
      if (filterState) params.set('state', filterState);
      if (since) params.set('since', since);
      const res = await fetch(`${API}/admin/outreach/stats/conversion?${params}`, { headers });
      if (!res.ok) throw new Error('Stats fehlgeschlagen');
      setData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterChannel, filterBranche, filterState, since, headers]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const t = data?.totals || { leads: 0, first_contacts: 0, responded: 0, meetings: 0, mandates: 0 };

  // Funnel
  const funnel = data ? [
    { label: 'Erstkontakte', value: t.first_contacts },
    { label: 'Antworten erhalten', value: t.responded },
    { label: 'Erstgespräche', value: t.meetings },
    { label: 'Mandate', value: t.mandates },
  ] : [];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.value));

  return (
    <div className="space-y-6" data-testid="stats-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 size={18} className="text-slate-700" />
            Erfolgs-Tracking
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Konversion und Resonanz pro Kanal, Branche und Region. Datenbasis sind Leads
            mit erfasster Resonanz im Lead-Detail-Modal.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
          {loading ? <Loader2 size={13} className="mr-2 animate-spin" /> : <RefreshCcw size={13} className="mr-2" />}
          Aktualisieren
        </Button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-3 items-center">
        <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500">Filter:</span>
        <select
          value={filterChannel}
          onChange={(e) => setFilterChannel(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white"
          data-testid="stats-filter-channel"
        >
          <option value="">Alle Kanäle</option>
          {CHANNEL_OPTIONS.filter((o) => o.value).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={filterBranche}
          onChange={(e) => setFilterBranche(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white"
          data-testid="stats-filter-branche"
        >
          <option value="">Alle Branchen</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Bundesland (z. B. BW)"
          value={filterState}
          onChange={(e) => setFilterState(e.target.value.toUpperCase().slice(0, 2))}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white w-32"
          data-testid="stats-filter-state"
        />
        <label className="text-xs text-slate-500 inline-flex items-center gap-1">
          ab
          <input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white"
            data-testid="stats-filter-since"
          />
        </label>
        {(filterChannel || filterBranche || filterState || since) && (
          <button
            onClick={() => { setFilterChannel(''); setFilterBranche(''); setFilterState(''); setSince(''); }}
            className="text-xs text-orange-600 hover:underline ml-auto"
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <KPI label="Leads" value={t.leads} testId="kpi-leads" />
        <KPI label="Erstkontakte" value={t.first_contacts} testId="kpi-first-contacts" />
        <KPI label="Antwortquote" value={`${t.response_rate_pct || 0}`} suffix="%" color="text-emerald-700" testId="kpi-response-rate" />
        <KPI label="Erstgespräche" value={t.meetings} suffix={`/ ${t.first_contacts}`} testId="kpi-meetings" />
        <KPI label="Mandate" value={t.mandates} suffix={t.meetings > 0 ? `(${t.conv_meeting_to_mandate_pct}%)` : ''} color="text-emerald-700" testId="kpi-mandates" />
      </div>

      {/* Funnel */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Akquise-Funnel</h3>
        {t.first_contacts === 0 ? (
          <p className="text-xs text-slate-400 italic">Noch keine Erstkontakte erfasst — sobald du den ersten Outcome im Lead-Detail-Modal speicherst, erscheint hier dein Funnel.</p>
        ) : (
          <div className="space-y-2.5">
            {funnel.map((f, i) => {
              const pct = funnelMax > 0 ? Math.max(2, Math.round((f.value / funnelMax) * 100)) : 0;
              const prev = i > 0 ? funnel[i - 1].value : 0;
              const drop = i > 0 && prev > 0 ? Math.round((1 - f.value / prev) * 100) : null;
              return (
                <div key={f.label}>
                  <div className="flex items-baseline justify-between text-xs mb-1">
                    <span className="font-medium text-slate-700">{f.label}</span>
                    <span className="text-slate-500">
                      {f.value}
                      {drop !== null && f.value > 0 && (
                        <span className="ml-2 text-slate-400">−{drop}% vs. vorherige Stufe</span>
                      )}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: i === funnel.length - 1 ? '#10b981' : '#FFA266',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sections */}
      <Section title="Performance pro Kanal" buckets={data?.by_channel} labelMap={CHANNEL_LABELS} />
      <Section title="Performance pro Branche" buckets={data?.by_branche} labelMap={TYPE_LABELS} />
      <Section title="Performance pro Bundesland" buckets={data?.by_state} />

      {/* Quality breakdown */}
      {data && Object.keys(data.by_response_quality || {}).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Antwort-Qualität</h3>
          <div className="space-y-2">
            {Object.entries(data.by_response_quality).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-slate-700">{QUALITY_LABELS[k] || k}</span>
                <span className="font-medium text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsTab;
